import { PlusIcon } from '@shopify/polaris-icons';
import { Page, Modal, TextField, Select } from '../components/forms';
import { useEffect, useState, useCallback } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  DataTable,
  Spinner,
  Checkbox,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

interface StorageFeeRule {
  id: number;
  name: string;
  description: string | null;
  trigger_type: string;
  grace_period_days: number;
  daily_rate: number;
  max_fee: number | null;
  currency: string;
  is_active: number;
  applies_to_all: number;
  zone_count: number;
  created_at: string;
}

interface StorageFeeCharge {
  id: number;
  order_id: number;
  shopify_order_number: string;
  customer_name: string;
  trigger_type: string;
  trigger_date: string;
  grace_end_date: string;
  daily_rate: number;
  days_charged: number;
  total_fee: number;
  status: string;
  rule_name: string;
}

interface Stats {
  status: string;
  count: number;
  total_amount: number;
}

const triggerTypeOptions = [
  { label: 'Failed Delivery', value: 'failed_delivery' },
  { label: 'Customer Refused', value: 'customer_refused' },
  { label: 'Reschedule Delay', value: 'reschedule_delay' },
  { label: 'Collection Delay', value: 'collection_delay' },
];

const triggerTypeLabels: Record<string, string> = {
  failed_delivery: 'Failed Delivery',
  customer_refused: 'Customer Refused',
  reschedule_delay: 'Reschedule Delay',
  collection_delay: 'Collection Delay',
};

export default function StorageFees() {
  const navigate = useNavigate();
  const { fetchApi, error, loading } = useApi();
  const [rules, setRules] = useState<StorageFeeRule[]>([]);
  const [charges, setCharges] = useState<StorageFeeCharge[]>([]);
  const [stats, setStats] = useState<Stats[]>([]);
  const [activeTab, setActiveTab] = useState<'rules' | 'charges'>('rules');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState<StorageFeeRule | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_type: 'failed_delivery',
    grace_period_days: '3',
    daily_rate: '5.00',
    max_fee: '50.00',
    is_active: true,
    applies_to_all: true,
  });
  const [saving, setSaving] = useState(false);

  const loadRules = useCallback(async () => {
    const result = await fetchApi<{ success: boolean; rules: StorageFeeRule[] }>('/storage-fees/rules');
    if (result?.success) {
      setRules(result.rules || []);
    }
  }, [fetchApi]);

  const loadCharges = useCallback(async () => {
    const result = await fetchApi<{ success: boolean; charges: StorageFeeCharge[]; stats: Stats[] }>('/storage-fees/charges?limit=50');
    if (result?.success) {
      setCharges(result.charges || []);
      setStats(result.stats || []);
    }
  }, [fetchApi]);

  useEffect(() => {
    loadRules();
    loadCharges();
  }, [loadRules, loadCharges]);

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(value);
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { tone: 'success' | 'warning' | 'info' | 'critical'; label: string }> = {
      accruing: { tone: 'warning', label: 'Accruing' },
      collected: { tone: 'success', label: 'Collected' },
      waived: { tone: 'info', label: 'Waived' },
      written_off: { tone: 'critical', label: 'Written Off' },
    };
    const { tone, label } = config[status] || { tone: 'info', label: status };
    return <Badge tone={tone}>{label}</Badge>;
  };

  const handleOpenModal = (rule?: StorageFeeRule) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        name: rule.name,
        description: rule.description || '',
        trigger_type: rule.trigger_type,
        grace_period_days: String(rule.grace_period_days),
        daily_rate: String(rule.daily_rate),
        max_fee: rule.max_fee ? String(rule.max_fee) : '',
        is_active: rule.is_active === 1,
        applies_to_all: rule.applies_to_all === 1,
      });
    } else {
      setEditingRule(null);
      setFormData({
        name: '',
        description: '',
        trigger_type: 'failed_delivery',
        grace_period_days: '3',
        daily_rate: '5.00',
        max_fee: '50.00',
        is_active: true,
        applies_to_all: true,
      });
    }
    setShowModal(true);
  };

  const handleSaveRule = async () => {
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        trigger_type: formData.trigger_type,
        grace_period_days: parseInt(formData.grace_period_days) || 0,
        daily_rate: parseFloat(formData.daily_rate) || 0,
        max_fee: formData.max_fee ? parseFloat(formData.max_fee) : null,
        is_active: formData.is_active,
        applies_to_all: formData.applies_to_all,
      };

      if (editingRule) {
        await fetchApi(`/storage-fees/rules/${editingRule.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await fetchApi('/storage-fees/rules', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      setShowModal(false);
      loadRules();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId: number) => {
    if (!confirm('Are you sure you want to delete this rule?')) return;

    await fetchApi(`/storage-fees/rules/${ruleId}`, { method: 'DELETE' });
    loadRules();
  };

  const handleCalculateAll = async () => {
    const result = await fetchApi<{ success: boolean; charges_updated: number }>('/storage-fees/calculate-all', {
      method: 'POST',
    });
    if (result?.success) {
      loadCharges();
    }
  };

  const rulesRows = rules.map((rule) => [
    <Text as="span" fontWeight="bold">{rule.name}</Text>,
    <Badge>{triggerTypeLabels[rule.trigger_type] || rule.trigger_type}</Badge>,
    `${rule.grace_period_days} days`,
    formatCurrency(rule.daily_rate),
    rule.max_fee ? formatCurrency(rule.max_fee) : 'No cap',
    rule.is_active ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge>,
    <InlineStack gap="200">
      <Button size="slim" onClick={() => handleOpenModal(rule)}>Edit</Button>
      <Button size="slim" tone="critical" onClick={() => handleDeleteRule(rule.id)}>Delete</Button>
    </InlineStack>,
  ]);

  const chargesRows = charges.map((charge) => [
    <Button variant="plain" onClick={() => navigate(`/orders/${charge.order_id}`)}>
      {charge.shopify_order_number}
    </Button>,
    charge.customer_name || '-',
    <Badge>{triggerTypeLabels[charge.trigger_type] || charge.trigger_type}</Badge>,
    new Date(charge.trigger_date).toLocaleDateString('en-GB'),
    `${charge.days_charged} days`,
    <Text as="span" fontWeight="bold">{formatCurrency(charge.total_fee)}</Text>,
    getStatusBadge(charge.status),
  ]);

  const accruingStats = stats.find(s => s.status === 'accruing');
  const collectedStats = stats.find(s => s.status === 'collected');

  return (
    <Page
      title="Storage Fees"
      subtitle="Manage storage fees for refused or delayed deliveries"
      backAction={{ content: 'Settings', onAction: () => navigate('/settings') }}
      primaryAction={{
        icon: PlusIcon, content: 'Add Rule',
        onAction: () => handleOpenModal(),
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Summary Cards */}
        <Layout.Section>
          <InlineStack gap="400" wrap={false}>
            <Card>
              <BlockStack gap="200">
                <Text as="span" variant="bodySm" tone="subdued">Active Fees</Text>
                <Text as="p" variant="heading2xl">{accruingStats?.count || 0}</Text>
                <Text as="span" variant="bodySm">
                  {formatCurrency(accruingStats?.total_amount || 0)} outstanding
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="span" variant="bodySm" tone="subdued">Collected</Text>
                <Text as="p" variant="heading2xl">{collectedStats?.count || 0}</Text>
                <Text as="span" variant="bodySm">
                  {formatCurrency(collectedStats?.total_amount || 0)} collected
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="span" variant="bodySm" tone="subdued">Active Rules</Text>
                <Text as="p" variant="heading2xl">{rules.filter(r => r.is_active).length}</Text>
                <Text as="span" variant="bodySm">
                  of {rules.length} total rules
                </Text>
              </BlockStack>
            </Card>
          </InlineStack>
        </Layout.Section>

        {/* Tab Buttons */}
        <Layout.Section>
          <InlineStack gap="200">
            <Button
              pressed={activeTab === 'rules'}
              onClick={() => setActiveTab('rules')}
            >
              Fee Rules
            </Button>
            <Button
              pressed={activeTab === 'charges'}
              onClick={() => setActiveTab('charges')}
            >
              {`Active Charges (${charges.filter(c => c.status === 'accruing').length})`}
            </Button>
          </InlineStack>
        </Layout.Section>

        {activeTab === 'rules' ? (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Storage Fee Rules</Text>

                {loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                    <Spinner />
                  </div>
                ) : rules.length === 0 ? (
                  <Banner tone="info">
                    No storage fee rules configured. Create a rule to start applying fees for refused or delayed deliveries.
                  </Banner>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'numeric', 'text', 'text', 'text']}
                    headings={['Name', 'Trigger', 'Grace Period', 'Daily Rate', 'Max Fee', 'Status', 'Actions']}
                    rows={rulesRows}
                  />
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
        ) : (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text as="h2" variant="headingMd">Storage Fee Charges</Text>
                  <Button onClick={handleCalculateAll}>Recalculate All</Button>
                </InlineStack>

                {loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                    <Spinner />
                  </div>
                ) : charges.length === 0 ? (
                  <Banner tone="info">
                    No storage fee charges yet. Fees will appear here when triggered by delivery failures or customer refusals.
                  </Banner>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'text']}
                    headings={['Order', 'Customer', 'Trigger', 'Started', 'Days', 'Total', 'Status']}
                    rows={chargesRows}
                  />
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Info Banner */}
        <Layout.Section>
          <Banner tone="info">
            <p><strong>How Storage Fees Work:</strong></p>
            <ul style={{ margin: '8px 0 0 16px' }}>
              <li><strong>Failed Delivery:</strong> Applied when delivery cannot be completed (not home, access issues)</li>
              <li><strong>Customer Refused:</strong> Applied when customer actively refuses to accept delivery</li>
              <li><strong>Reschedule Delay:</strong> Applied when customer delays rescheduling beyond grace period</li>
              <li><strong>Collection Delay:</strong> Applied when click-and-collect orders exceed waiting time</li>
            </ul>
          </Banner>
        </Layout.Section>
      </Layout>

      {/* Add/Edit Rule Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingRule ? 'Edit Storage Fee Rule' : 'Add Storage Fee Rule'}
        primaryAction={{
          icon: editingRule ? undefined : PlusIcon, content: 'Save',
          onAction: handleSaveRule,
          loading: saving,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setShowModal(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Rule Name"
              requiredIndicator value={formData.name}
              onChange={(value) => setFormData({ ...formData, name: value })}
              autoComplete="off"
              placeholder="e.g., Standard Storage Fee"
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(value) => setFormData({ ...formData, description: value })}
              autoComplete="off"
              multiline={2}
              placeholder="When this fee applies..."
            />

            <Select
              label="Trigger Type"
              options={triggerTypeOptions}
              requiredIndicator value={formData.trigger_type}
              onChange={(value) => setFormData({ ...formData, trigger_type: value })}
            />

            <TextField
              label="Grace Period (days)"
              type="number"
              value={formData.grace_period_days}
              onChange={(value) => setFormData({ ...formData, grace_period_days: value })}
              autoComplete="off"
              helpText="Days before storage fees start accruing"
            />

            <TextField
              label="Daily Rate (EUR)"
              type="number"
              requiredIndicator value={formData.daily_rate}
              onChange={(value) => setFormData({ ...formData, daily_rate: value })}
              autoComplete="off"
              prefix="€"
            />

            <TextField
              label="Maximum Fee (EUR)"
              type="number"
              value={formData.max_fee}
              onChange={(value) => setFormData({ ...formData, max_fee: value })}
              autoComplete="off"
              prefix="€"
              helpText="Leave empty for no cap"
            />

            <Checkbox
              label="Rule is active"
              checked={formData.is_active}
              onChange={(value) => setFormData({ ...formData, is_active: value })}
            />

            <Checkbox
              label="Applies to all delivery zones"
              checked={formData.applies_to_all}
              onChange={(value) => setFormData({ ...formData, applies_to_all: value })}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
